import {
  lazy,
  Suspense,
  type FunctionComponent,
  type PropsWithChildren,
} from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import Spinner from '@atlaskit/spinner';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import MCPFooter from './MCPFooter';
import MCP from './MCP';
import ConsentForm from '../../Flow/ConsentForm';
import { McpContextProvider, useMcpContext } from './McpContextProvider';
import { useToken } from '../../Flow/ConsentContextProvider';
import { classifyScopes } from './domain/trelloScopes';
import {
  agentMcpMetadataError,
  useAgentMcpMetadata,
  type AgentMcpDisplayMetadata,
} from './domain/useAgentMcpMetadata';

/**
 * Lazily imported so the AGG/Relay `graphql` tag it pulls in is only evaluated
 * when a Trello token actually renders it. Importing it eagerly would put Relay
 * in the import graph of everything that reaches this module, including consent
 * flows that never touch Trello and the unit tests covering them.
 *
 * The Suspense boundary below already exists for the query itself, so it covers
 * the chunk load too and there is no extra fallback state to design.
 */
const TrelloWorkspaceFetcher = lazy(() => import('./TrelloWorkspaceFetcher'));

const mcpConsentMainStyle = xcss({
  backgroundColor: 'elevation.surface',
  boxSizing: 'border-box',
  padding: 'space.0',
  flex: '1 1 0%',
  minWidth: '0',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
});

const mcpMainViewportStyle = xcss({
  display: 'flex',
  flexDirection: 'column',
  flex: '0 1 auto',
  minHeight: '0',
  width: '100%',
});

const mcpFormFlexStyle = xcss({
  display: 'flex',
  flexDirection: 'column',
  flex: '0 1 auto',
  minHeight: '0',
  width: '100%',
});

const contentGrowStyle = xcss({
  flex: '0 1 auto',
  minWidth: '0',
  display: 'flex',
  flexDirection: 'column',
});

const agentMcpLoadingStyle = xcss({
  width: '100%',
  minHeight: 'calc(100vh - 56px)',
});

const ContentGrowWrapper: FunctionComponent<PropsWithChildren> = ({
  children,
}) => <Box xcss={contentGrowStyle}>{children}</Box>;

/** Inner layout: the consent form, its content, and the fixed footer. */
const McpConsentInner: FunctionComponent<{
  agentMetadata?: AgentMcpDisplayMetadata;
}> = ({ agentMetadata }) => {
  return (
    <Box xcss={[mcpConsentMainStyle, mcpMainViewportStyle]}>
      <Box xcss={mcpFormFlexStyle}>
        <ConsentForm>
          <ContentGrowWrapper>
            <MCP agentMetadata={agentMetadata} />
          </ContentGrowWrapper>
          <MCPFooter />
        </ConsentForm>
      </Box>
    </Box>
  );
};

/** Stands in for the whole consent screen while the agent loads. */
const AgentMcpLoading: FunctionComponent = () => (
  <Flex xcss={agentMcpLoadingStyle} alignItems="center" justifyContent="center">
    <Spinner size="large" label="Loading agent" />
  </Flex>
);

const AgentMcpConsentInner: FunctionComponent<{
  agentAri: string;
}> = ({ agentAri }) => {
  const agentMetadata = useAgentMcpMetadata(agentAri);
  return <McpConsentInner agentMetadata={agentMetadata} />;
};

/**
 * Agent MCP shows no consent content until the agent loads, and any failure
 * shows the consent error page. Relay's raw errors are not consent errors,
 * which ErrorPage rethrows, so the local boundary throws one to the app
 * boundary instead (a boundary cannot catch its own fallback).
 */
const McpConsent: FunctionComponent = () => {
  const { agentMcpResource } = useMcpContext();
  const { clientName } = useToken();
  if (!agentMcpResource) {
    return <McpConsentInner />;
  }
  return (
    <ErrorBoundary
      fallbackRender={() => {
        throw agentMcpMetadataError(clientName);
      }}
    >
      <Suspense fallback={<AgentMcpLoading />}>
        <AgentMcpConsentInner agentAri={agentMcpResource} />
      </Suspense>
    </ErrorBoundary>
  );
};

const McpConsentLayout: FunctionComponent = () => {
  const token = useToken();
  const { hasTrello } = classifyScopes(token.scopes ?? []);

  if (hasTrello) {
    return (
      <Suspense
        fallback={
          <McpContextProvider trelloWorkspacesLoading>
            <McpConsent />
          </McpContextProvider>
        }
      >
        <TrelloWorkspaceFetcher>
          <McpConsent />
        </TrelloWorkspaceFetcher>
      </Suspense>
    );
  }

  return (
    <McpContextProvider>
      <McpConsent />
    </McpContextProvider>
  );
};

export default McpConsentLayout;
